import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Text, Table } from "@chakra-ui/react";
import {
    OnChangeFn,
    PaginationState,
    SortingState,
    VisibilityState,
    getCoreRowModel,
    getExpandedRowModel,
    useReactTable
} from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

import { useQuery } from "@tanstack/react-query";
import { NormalizedSearchResultDto } from "@/api/claimsInvoicingApi";
import { InvoiceTableDataDto } from "@/api/invoiceApi";
import { getHierarchyQueryOption } from "@/api/queryOptions/usersAPIQueryOptions";

import { useColumns } from "./Columns";
import TableContent from "../../../table/TableContent";
import Pagination from "../../../table/Pagination";
import InvoicesTableRow from "../../../table/InvoicesTableRow";

type DraftInvoicesTableProps = {
    invoicesResult: NormalizedSearchResultDto<InvoiceTableDataDto> | undefined;
    pageIndex: number;
    pageSize: number;
    setPagination: OnChangeFn<PaginationState>;
    sorting: SortingState;
    setSorting: OnChangeFn<SortingState>;
    isFetchingInvoices: boolean;
};

export const DraftInvoicesTable = React.memo(function DraftInvoicesTable({
    invoicesResult,
    pageIndex,
    pageSize,
    setPagination,
    sorting,
    setSorting,
    isFetchingInvoices
}: DraftInvoicesTableProps) {
    const { t } = useTranslation("claimsInvoicesOverview");

    const { data: branchCompanies } = useQuery(getHierarchyQueryOption());

    const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({
        branchCompany: branchCompanies?.children !== null
    });

    useEffect(() => {
        setColumnVisibility({
            branchCompany: branchCompanies?.children !== null
        });
    }, [branchCompanies]);

    const pagination = useMemo(
        () => ({
            pageIndex,
            pageSize
        }),
        [pageIndex, pageSize]
    );

    const handleSortingChange = useCallback(
        (sortingValue: SortingState | ((old: SortingState) => SortingState)) => {
            setSorting(sortingValue);
            setPagination({ pageIndex: 0, pageSize });
        },
        [setSorting, setPagination, pageSize]
    );

    const columns = useColumns(branchCompanies);

    const table = useReactTable<InvoiceTableDataDto>({
        columns,
        data: invoicesResult?.items ?? [],
        pageCount: Math.max(1, Math.ceil((invoicesResult?.totalResults ?? 0) / pageSize)),
        state: {
            pagination,
            sorting,
            columnVisibility
        },
        onPaginationChange: setPagination,
        manualPagination: true,
        manualSorting: true,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        onSortingChange: handleSortingChange,
        onColumnVisibilityChange: setColumnVisibility,
        enableSortingRemoval: false
    });
    const rowCount = invoicesResult?.totalResults ?? 0;

    return (
        <Table.ScrollArea
            data-testid="draftInvoicesTable"
            overflowY="auto"
            border="2px solid"
            borderRadius="5px"
            borderColor={"anchor.brown.200"}
            maxH="60rem"
        >
            <TableContent table={table}>
                {table.getRowModel().rows.map(row => (
                    <InvoicesTableRow key={row.original.id} row={row} />
                ))}
            </TableContent>
            {!invoicesResult?.items?.length && !isFetchingInvoices && (
                <Text justifySelf="center" padding="1.5rem" data-testid="emptyInvoicesList">
                    {t("emptyInvoicesList")}
                </Text>
            )}
            <Pagination id="draftInvoiceTable" table={table} rowCount={rowCount} />
        </Table.ScrollArea>
    );
});
